import React, { useState } from 'react';
import useAuth from '../../hooks/useAuth';

/**
 * Banner shown when the user is signed in but their email is not verified.
 * Encourages verification and offers a resend link.
 */
const EmailVerificationBanner: React.FC = () => {
  const { user, sendVerificationEmail, refreshAuthState } = useAuth();
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (!user || user.emailVerified || dismissed) {
    return null;
  }

  const handleResend = async () => {
    setResendLoading(true);
    try {
      await sendVerificationEmail();
      setResendSent(true);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div
      className="d-flex align-items-center justify-content-between flex-wrap gap-2 px-3 py-2"
      style={{
        backgroundColor: 'var(--tv-warning-bg, rgba(245, 158, 11, 0.15))',
        borderBottom: '1px solid var(--tv-warning-border, rgba(245, 158, 11, 0.4))',
        color: 'var(--tv-warning-text, #fef3c7)',
      }}
      role="alert"
    >
      <span className="small">
        Please verify your email ({user.email}). Check your inbox for the link.
      </span>
      <div className="d-flex align-items-center gap-2">
        <button
          type="button"
          className="btn btn-sm"
          style={{ backgroundColor: 'var(--tv-warning-button-bg, rgba(245, 158, 11, 0.3))', borderColor: 'var(--tv-warning-border, #f59e0b)', color: '#fff' }}
          onClick={handleResend}
          disabled={resendLoading || resendSent}
        >
          {resendLoading ? 'Sending…' : resendSent ? 'Sent' : 'Resend email'}
        </button>
        <button
          type="button"
          className="btn btn-sm btn-link p-0"
          style={{ color: 'var(--tv-warning-text, #fef3c7)' }}
          onClick={() => {
            refreshAuthState();
          }}
          title="Refresh if you already verified"
        >
          I verified
        </button>
        <button
          type="button"
          className="btn btn-sm btn-link p-0"
          style={{ color: 'var(--tv-text-secondary, #94a3b8)' }}
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          title="Dismiss"
        >
          ×
        </button>
      </div>
    </div>
  );
};

export default EmailVerificationBanner;
